import { alpha } from "@mui/material/styles"
import fp from "lodash/fp"

import type { Palette, PaletteColor } from "@mui/material/styles"

export const baseGreys = {
  100: "#F3F4F6",
  200: "#E5E7EB",
  300: "#D1D5DB",
  400: "#9CA3AF",
  500: "#6B7280",
  600: "#4B5563",
  700: "#272E3A",
  800: "#191F28",
  900: "#11151C",
}

export const extraColors = [
  { name: "limeGreen", hex: "#93F446" },
  { name: "purple", hex: "#9B37E9" },
  { name: "blue", hex: "#2187FF" },
  { name: "orangePeach", hex: "#F48F46" },
  { name: "skyBlue", hex: "#53DDF0" },
  { name: "hotPink", hex: "#D635CF" },
  { name: "chartreuse", hex: "#AADA22" },
  { name: "coral", hex: "#FF3D83" },
  { name: "periwinkle", hex: "#565CED" },
  { name: "amber", hex: "#F4AF46" },
  { name: "tealGreen", hex: "#12CB94" },
  { name: "lilac", hex: "#E499FF" },
  { name: "orange", hex: "#FF7512" },
  { name: "babyBlue", hex: "#8FC3FF" },
  { name: "rosePink", hex: "#ED56B0" },
  { name: "neonLime", hex: "#D2FC2D" },
  { name: "softIndigo", hex: "#A6AEFF" },
  { name: "lavender", hex: "#897FFF" },
  { name: "magenta", hex: "#E05CEC" },
  { name: "goldenrod", hex: "#DBBE23" },
  { name: "orchid", hex: "#AB7DD9" },
  { name: "neonPink", hex: "#F818AC" },
  { name: "mintGreen", hex: "#57C068" },
  { name: "cinnabar", hex: "#FF4800" },
  { name: "tangerine", hex: "#FF7900" },
  { name: "marigold", hex: "#FFB600" },
] as const

export type ExtraColor = (typeof extraColors)[number]["name"]

export const extraColorList = extraColors.map(({ hex }) => hex)

export const buildExtrasPalette = (
  palette: Palette,
): { [key in ExtraColor]: PaletteColor } =>
  fp.mapValues(
    ({ hex }: { hex: string; name: string }) =>
      palette.augmentColor({
        color: { main: hex, light: alpha(hex, 0.15) },
      }),
    fp.keyBy((ec) => ec.name, extraColors),
  ) as { [key in ExtraColor]: PaletteColor }

export const deviceStatusColors = [
  { name: "reporting", hex: "#42B38E" },
  { name: "active", hex: "#009fe3" },
  { name: "intermittent", hex: "#F9B233" },
  { name: "inactive", hex: "#FF7512" },
  { name: "unknown", hex: baseGreys[500] },
] as const

export type DeviceStatusColor = (typeof deviceStatusColors)[number]["name"]

export const deviceStatusColorList = deviceStatusColors.map(({ hex }) => hex)

export const buildDeviceStatusPalette = (
  palette: Palette,
): { [key in DeviceStatusColor]: PaletteColor } =>
  fp.mapValues(
    ({ hex }: { hex: string; name: string }) =>
      palette.augmentColor({
        color: { main: hex, light: alpha(hex, 0.15) },
      }),
    fp.keyBy((dsc) => dsc.name, deviceStatusColors),
  ) as { [key in DeviceStatusColor]: PaletteColor }

/**
 * How often a span is the limiting one, palest to hottest. Ordered: the index is
 * the scale, so `spanFrequency.ramp[n]` is the nth band and nothing else needs to
 * know the hexes. It darkens as it warms, so the scale survives colorblindness.
 */
export const spanFrequencyRamp = [
  "#ffe59485",
  "#ffd035",
  "#FF9800",
  "#ff7a22",
  "#ff5202",
  "#e92300",
] as const

// The rest of the limiting-span views: everything that isn't a frequency.
export const spanStateColors = {
  // Span with no frequency to show — an empty lane, or a route with no data.
  none: "#8b93a355",
  // A span that never limited the line. Grey enough to sit back, dark enough
  // to follow.
  route: "#6b7488",
  // Below-threshold span in the overview strip, so the frequent few stand out.
  overview: "#39424f7a",
  // Hovered or selected span, in every view.
  hover: "#0BBBEF",
} as const

export type SpanStateColor = keyof typeof spanStateColors

export const qualityColors = [
  { name: "healthy", hex: "#10b981" },
  { name: "degraded", hex: "#f59e0b" },
  { name: "unreliable", hex: "#f97316" },
  { name: "invalid", hex: "#ef4444" },
] as const

export type QualityColor = (typeof qualityColors)[number]["name"]

export const qualityColorList = qualityColors.map(({ hex }) => hex)

export const buildQualityPalette = (
  palette: Palette,
): { [key in QualityColor]: PaletteColor } =>
  fp.mapValues(
    ({ hex }: { hex: string; name: string }) =>
      palette.augmentColor({
        color: { main: hex, light: alpha(hex, 0.15) },
      }),
    fp.keyBy((qc) => qc.name, qualityColors),
  ) as { [key in QualityColor]: PaletteColor }
